import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SECURITY_QUESTIONS from "@/constants/securityQuestions";
import { Mail, Lock, Phone, Cake, ArrowLeft, User } from "lucide-react";
import WelcomeSplash from "@/components/WelcomeSplash";

const HERO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_dorm-attendance-4/artifacts/wz7p0qxp_IMG_20260927_140020.png";
const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_dorm-attendance-4/artifacts/c3wpw9lm_images%20%288%29.jpeg";
const APP_NAME = "Sistem Pengurusan Asrama (TeAm) SMKMI";
export default function Login() {
  const { login, loginAsVisitor, signup } = useAuth();
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem("smkmi_welcomed"));
  const dismissSplash = useCallback(() => {
    sessionStorage.setItem("smkmi_welcomed", "1");
    setShowSplash(false);
  }, []);
  const [mode, setMode] = useState("login"); // login | signup | forgot | change-password
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [changeSecurityQuestion, setChangeSecurityQuestion] = useState("");
  const [changeQuestionLoaded, setChangeQuestionLoaded] = useState(false);
  const [form, setForm] = useState({
    name: "", short_name: "", email: "", password: "", phone: "", birthday: "", staff_role: "",
    security_question: "", security_answer: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    if (!form.staff_role) {
      toast.error("Please select a role");
      return;
    }
    if (!form.security_question || !form.security_answer.trim()) {
      toast.error("Please choose a security question and enter an answer");
      return;
    }
    setBusy(true);
    try {
      await signup(form);
      toast.success("Account created!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!found) {
        const res = await api.post("/auth/forgot", { email: form.email });
        setFound(res.data);
      } else {
        const response = await api.post("/auth/forgot/reset", {
          email: form.email,
          security_answer: securityAnswer,
          new_password: resetPassword,
        });
        toast.success(response.data.message || "Password reset successfully");
        setForm({ ...form, password: "" });
        setFound(null);
        setSecurityAnswer("");
        setResetPassword("");
        setMode("login");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (found ? "Unable to reset password" : "Unable to find account"));
    } finally {
      setBusy(false);
    }
  };

  const doChangePassword = async (e) => {
    e.preventDefault();
    if (!changeQuestionLoaded) {
      toast.error("Look up your security question first");
      return;
    }
    if (!changeSecurityQuestion) {
      toast.error("Choose a security question for this account");
      return;
    }
    setBusy(true);
    try {
      const response = await api.post("/auth/change-password-at-login", {
        email: form.email,
        current_password: form.password,
        new_password: newPassword,
        security_answer: securityAnswer,
        security_question: changeSecurityQuestion || undefined,
      });
      setForm({ ...form, password: "" });
      setNewPassword("");
      setSecurityAnswer("");
      setChangeSecurityQuestion("");
      setChangeQuestionLoaded(false);
      setMode("login");
      toast.success(response.data.message || "Password updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to change password");
    } finally {
      setBusy(false);
    }
  };

  const loadChangeSecurityQuestion = async () => {
    if (!form.email) {
      toast.error("Enter your registered email first");
      return;
    }
    setBusy(true);
    try {
      const response = await api.post("/auth/forgot", { email: form.email });
      setChangeSecurityQuestion(response.data.security_question || "");
      setChangeQuestionLoaded(true);
      setSecurityAnswer("");
    } catch (error) {
      setChangeQuestionLoaded(false);
      toast.error(error.response?.data?.detail || "Unable to load the security question");
    } finally {
      setBusy(false);
    }
  };

  const field = (icon, props) => (
    props.type === "password" ? (
      <PasswordInput icon={icon} className="h-11" {...props} />
    ) : (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{icon}</span>
        <Input {...props} className={`pl-10 h-11 ${props.className || ""}`} />
      </div>
    )
  );

  if (showSplash) return <WelcomeSplash onDone={dismissSplash} />;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Visual side */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Campus" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/90 via-[#1D4ED8]/80 to-[#1E3A8A]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.18),_transparent_28%)]" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3 rounded-full border border-white/20 bg-white/5 px-4 py-2 backdrop-blur-sm shadow-lg shadow-blue-950/20">
            <img src={LOGO} alt="Logo SMKMI" className="h-12 w-12 rounded-full object-cover bg-white p-0.5 shadow-md" />
            <span className="font-display font-extrabold text-xl leading-tight tracking-tight">{APP_NAME}</span>
          </div>
          <div className="max-w-xl">
            <div className="mb-4 inline-flex items-center rounded-full border border-[#FACC15]/40 bg-[#FACC15]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[#FDE68A]">
              Hostel Management
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-lg">
              Asrama SMKMI,<br /><span className="text-[#FACC15]">Penginapan Atlet Cemerlang</span>
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-blue-50/85">
              Selamat datang ke hab pengurusan kediaman atlet yang moden dan serba lengkap
            </p>
          </div>
          <p className="text-sm text-blue-100/75 tracking-[0.14em] uppercase">Smart Campus Residence Management</p>
        </div>
      </div>

      {/* Form side */}
      <div className="relative flex items-center justify-center p-6 sm:p-10 pb-16 bg-slate-50">
        {/* Mobile-only background image */}
        <div className="lg:hidden absolute inset-0" aria-hidden="true">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/90 via-[#1D4ED8]/80 to-[#0F172A]/90" />
        </div>
        <div className="relative z-10 w-full max-w-md rounded-[28px] border border-blue-100 bg-white/95 p-6 shadow-[0_25px_60px_rgba(37,99,235,0.12)] backdrop-blur-md lg:rounded-[30px] lg:border-slate-200 lg:bg-white lg:p-8 lg:shadow-[0_30px_80px_rgba(15,23,42,0.12)]">
          <div className="lg:hidden flex items-center gap-2 mb-8 rounded-full border border-blue-100 bg-blue-50/80 px-3 py-2 shadow-sm">
            <img src={LOGO} alt="Logo SMKMI" className="h-11 w-11 rounded-full object-cover bg-white flex-shrink-0 shadow" />
            <span className="font-display font-extrabold text-base leading-tight text-slate-800">{APP_NAME}</span>
          </div>

          {mode === "login" && (
            <form onSubmit={doLogin} className="space-y-5 animate-fade-up">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.22em] text-blue-600">Welcome back</p>
                <h2 className="font-display text-3xl font-extrabold text-slate-900">Sign in</h2>
                <p className="mt-1 text-sm text-slate-500">Use your registered email and password.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Email</Label>
                {field(<Mail className="h-4 w-4" />, { type: "email", required: true, value: form.email, onChange: set("email"), placeholder: "you@example.com", className: "border-slate-200 focus:border-blue-500 focus:ring-blue-200", "data-testid": "login-email-input" })}
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Password</Label>
                {field(<Lock className="h-4 w-4" />, { type: "password", required: true, value: form.password, onChange: set("password"), placeholder: "••••••••", className: "border-slate-200 focus:border-blue-500 focus:ring-blue-200", "data-testid": "login-password-input" })}
              </div>
              <Button type="submit" disabled={busy} className="w-full h-12 text-base font-semibold rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/20 hover:from-orange-600 hover:to-amber-600" data-testid="login-submit-button">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
              <Button type="button" variant="secondary" disabled={busy} className="w-full h-12 text-base font-medium rounded-xl border border-orange-600 bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/20 hover:from-orange-600 hover:to-amber-600" onClick={async () => {
                setBusy(true);
                try {
                  await loginAsVisitor();
                  toast.success("Signed in as visitor");
                  navigate("/student-outing");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Visitor login failed");
                } finally {
                  setBusy(false);
                }
              }} data-testid="visitor-login-button">
                Login as Visitor
              </Button>
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-sm">
                <button type="button" className="text-primary font-medium hover:underline" onClick={() => setMode("forgot")} data-testid="goto-forgot-link">
                  Forgot password?
                </button>
                <button type="button" className="text-primary font-medium hover:underline" onClick={() => setMode("change-password")} data-testid="goto-change-password-link">
                  Change password
                </button>
                <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setMode("signup")} data-testid="goto-signup-link">
                  Create account →
                </button>
              </div>
            </form>
          )}

          {mode === "signup" && (
            <form onSubmit={doSignup} className="space-y-4 animate-fade-up">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">Join the hostel</p>
                <h2 className="font-display text-3xl font-extrabold">Create account</h2>
              </div>
              <div className="space-y-2">
                <Label>Full name</Label>
                {field(<User className="h-4 w-4" />, { required: true, value: form.name, onChange: set("name"), placeholder: "Ahmad bin Ali", "data-testid": "signup-name-input" })}
              </div>
              <div className="space-y-2">
                <Label>Short name</Label>
                {field(<User className="h-4 w-4" />, { required: true, value: form.short_name, onChange: set("short_name"), placeholder: "Ahmad", "data-testid": "signup-short-name-input" })}
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.staff_role} onValueChange={(value) => setForm({ ...form, staff_role: value })}>
                  <SelectTrigger className="h-11" data-testid="signup-role-select">
                    <SelectValue placeholder="Choose a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Principal">Principal</SelectItem>
                    <SelectItem value="Hostel Supervisor">Hostel Supervisor</SelectItem>
                    <SelectItem value="Dining Hall Supervisor">Dining Hall Supervisor</SelectItem>
                    <SelectItem value="Warden">Warden</SelectItem>
                    <SelectItem value="Guard">Guard</SelectItem>
                    <SelectItem value="General Worker">General Worker</SelectItem>
                    <SelectItem value="Visitor">Visitor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                {field(<Mail className="h-4 w-4" />, { type: "email", required: true, value: form.email, onChange: set("email"), placeholder: "you@example.com", "data-testid": "signup-email-input" })}
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                {field(<Lock className="h-4 w-4" />, { type: "password", required: true, value: form.password, onChange: set("password"), placeholder: "Choose a password", "data-testid": "signup-password-input" })}
              </div>
              <div className="space-y-2">
                <Label>Phone number</Label>
                {field(<Phone className="h-4 w-4" />, { required: true, value: form.phone, onChange: set("phone"), placeholder: "+60 12-345 6789", "data-testid": "signup-phone-input" })}
              </div>
              <div className="space-y-2">
                <Label>Birthday</Label>
                {field(<Cake className="h-4 w-4" />, { type: "date", required: true, value: form.birthday, onChange: set("birthday"), "data-testid": "signup-birthday-input" })}
              </div>
              <div className="space-y-2">
                <Label>Security question</Label>
                <Select value={form.security_question} onValueChange={(value) => setForm({ ...form, security_question: value })}>
                  <SelectTrigger className="h-11" data-testid="signup-security-question-select"><SelectValue placeholder="Choose a security question" /></SelectTrigger>
                  <SelectContent>{SECURITY_QUESTIONS.map((question) => <SelectItem key={question} value={question}>{question}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Security answer</Label>
                {field(<Lock className="h-4 w-4" />, { type: "password", required: true, value: form.security_answer, onChange: set("security_answer"), placeholder: "Your answer", autoComplete: "off", "data-testid": "signup-security-answer-input" })}
                <p className="text-xs text-muted-foreground">Remember your answer; it is needed to recover or change your password.</p>
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 text-base" data-testid="signup-submit-button">
                {busy ? "Creating…" : "Create account"}
              </Button>
              <button type="button" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" onClick={() => setMode("login")} data-testid="back-to-login-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={doForgot} className="space-y-5 animate-fade-up">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">Recover access</p>
                <h2 className="font-display text-3xl font-extrabold">Forgot password</h2>
                <p className="text-muted-foreground text-sm mt-1">Verify your security answer to choose a new password.</p>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                {field(<Mail className="h-4 w-4" />, { type: "email", required: true, value: form.email, onChange: (event) => { setForm({ ...form, email: event.target.value }); setFound(null); setSecurityAnswer(""); setResetPassword(""); }, placeholder: "you@example.com", "data-testid": "forgot-email-input" })}
              </div>
              <Button type="submit" disabled={busy || Boolean(found && !found.security_question)} className="w-full h-11" data-testid="forgot-submit-button">
                {busy ? (found ? "Resetting…" : "Looking up…") : (found ? "Reset password" : "Find security question")}
              </Button>

              {found && (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 animate-fade-up" data-testid="forgot-result">
                  {found.security_question ? (
                    <>
                      <p className="text-sm font-semibold">{found.security_question}</p>
                      <div className="mt-3 space-y-2">
                        <Label htmlFor="forgot-security-answer">Your answer</Label>
                        <PasswordInput id="forgot-security-answer" value={securityAnswer} onChange={(event) => setSecurityAnswer(event.target.value)} required autoComplete="off" data-testid="forgot-security-answer-input" />
                      </div>
                      <div className="mt-3 space-y-2">
                        <Label htmlFor="forgot-new-password">New password</Label>
                        <PasswordInput id="forgot-new-password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} required minLength={6} autoComplete="new-password" data-testid="forgot-new-password-input" />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">This account has no security question yet. Sign in and change your password to set one, or contact an administrator for help.</p>
                  )}
                </div>
              )}

              <button type="button" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" onClick={() => setMode("login")} data-testid="forgot-back-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </form>
          )}

          {mode === "change-password" && (
            <form onSubmit={doChangePassword} className="space-y-5 animate-fade-up">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2">Account security</p>
                <h2 className="font-display text-3xl font-extrabold">Change password</h2>
                <p className="text-muted-foreground text-sm mt-1">Confirm your current password and security answer.</p>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                {field(<Mail className="h-4 w-4" />, { type: "email", required: true, value: form.email, onChange: (event) => { setForm({ ...form, email: event.target.value }); setChangeQuestionLoaded(false); setChangeSecurityQuestion(""); setSecurityAnswer(""); }, placeholder: "you@example.com", autoComplete: "username", "data-testid": "change-password-email-input" })}
              </div>
              {!changeQuestionLoaded ? (
                <Button type="button" variant="outline" disabled={busy} onClick={loadChangeSecurityQuestion} data-testid="load-change-security-question-button">Show security question</Button>
              ) : changeSecurityQuestion ? (
                <p className="rounded-md border border-border bg-muted/30 p-3 text-sm">{changeSecurityQuestion}</p>
              ) : (
                <div className="space-y-2">
                  <Label>Choose a security question</Label>
                  <Select value={changeSecurityQuestion} onValueChange={setChangeSecurityQuestion}>
                    <SelectTrigger className="h-11"><SelectValue placeholder="Choose a security question" /></SelectTrigger>
                    <SelectContent>{SECURITY_QUESTIONS.map((question) => <SelectItem key={question} value={question}>{question}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">This account will save the question and answer when you change its password.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Current password</Label>
                {field(<Lock className="h-4 w-4" />, { type: "password", required: true, value: form.password, onChange: set("password"), autoComplete: "current-password", "data-testid": "change-password-current-input" })}
              </div>
              <div className="space-y-2">
                <Label>Security answer</Label>
                {field(<Lock className="h-4 w-4" />, { type: "password", required: true, value: securityAnswer, onChange: (event) => setSecurityAnswer(event.target.value), autoComplete: "off", "data-testid": "change-password-security-answer-input" })}
              </div>
              <div className="space-y-2">
                <Label>New password</Label>
                {field(<Lock className="h-4 w-4" />, { type: "password", required: true, minLength: 6, value: newPassword, onChange: (event) => setNewPassword(event.target.value), autoComplete: "new-password", "data-testid": "change-password-new-input" })}
                <p className="text-xs text-muted-foreground">Use at least 6 characters.</p>
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 text-base" data-testid="change-password-submit-button">
                {busy ? "Updating…" : "Update password"}
              </Button>
              <button type="button" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" onClick={() => setMode("login")} data-testid="change-password-back-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
